import { Button } from '@src/components/ui/button';
import { cn } from '@src/lib/utils';
import type { ReactNode } from 'react';

/** Shared by every screen's filter bar (Runs, Enrichments, Overview) -- a labeled group of chips. */
export function FilterGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <div className="flex flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}

interface FilterChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
  /** Renders the chip inert (e.g. an Outcome value that can never occur in this screen's data) --
   * still visible, so its absence doesn't read as a bug, but not selectable. */
  disabled?: boolean;
  /** Tooltip explaining why the chip is disabled -- ignored when `disabled` is false. */
  title?: string;
}

export function FilterChip({ label, active, onClick, disabled, title }: FilterChipProps) {
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'h-auto rounded-full border px-2.5 py-1 text-[11.5px] font-medium transition-colors hover:bg-transparent',
        disabled
          ? 'cursor-not-allowed border-input text-muted-foreground/40'
          : active
            ? 'border-primary bg-primary/15 text-foreground hover:bg-primary/15'
            : 'border-input text-muted-foreground hover:border-border hover:text-foreground',
      )}
    >
      {label}
    </Button>
  );
}
